import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000/api";

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token and prevent requests when not authenticated
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  
  // Allow public endpoints without authentication
  const isLoginEndpoint = config.url?.includes("/auth/login") || config.url?.includes("/login");
  const isPublicPaymentEndpoint = config.url?.match(/^\/payments\/[^/]+$/); // Matches /payments/:id
  const isPublicCustomerEndpoint = config.url?.match(/^\/customers\/[^/]+$/); // Matches /customers/:id
  
  if (!token && !isLoginEndpoint && !isPublicPaymentEndpoint && !isPublicCustomerEndpoint) {
    // Cancel the request
    return Promise.reject(new Error("No authentication token found"));
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Extracts the message the API actually sent.
 *
 * The backend replies `{ status, message, errors }` on failure, but axios
 * rejects with its own generic Error whose `.message` is only ever
 * "Request failed with status code 400". Reading `error.message` therefore
 * hides the real reason ("Date range exceeds maximum allowed period of 90
 * days") behind a status code the user can do nothing with.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { message?: unknown } } })
    ?.response;
  const apiMessage = response?.data?.message;
  if (typeof apiMessage === "string" && apiMessage.trim()) {
    return apiMessage;
  }

  const genericMessage = (error as { message?: unknown })?.message;
  if (typeof genericMessage === "string" && genericMessage.trim()) {
    return genericMessage;
  }

  return fallback;
}

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is a login endpoint - don't redirect for login failures
      const isLoginEndpoint = error.config?.url?.includes("/auth/login") || error.config?.url?.includes("/login");
      
      if (!isLoginEndpoint && typeof window !== "undefined") {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        // Redirect to login on 401 unauthorized (except for login endpoint)
        window.location.href = "/login";
      }
      // For login endpoint, let the error propagate so it can be handled by the login flow
    }
    return Promise.reject(error);
  }
);
